// src/validation/organizationValidation.ts
import * as yup from 'yup';
import { AnyYupSchema, buildSchemaFromBase, BuildSchemaOptions } from './validationBuilder';

type OrganizationBaseFieldKey =
    | 'full_title'
    | 'short_title'
    | 'email'
    | 'password'
    | 'confirm_password'
    | 'phone'

type OrganizationContactFieldKey =
    | 'first_name'
    | 'second_name'
    | 'last_name'
    | 'contact_email'
    | 'contact_phone'
    | 'contact_person_position'

/* Поля организации */
export type OrganizationFieldKey = OrganizationBaseFieldKey | OrganizationContactFieldKey

/* Регексы */
const ORG_NAME_RE = new RegExp(
  [
    // не только пробелы
    '^(?!\\s+$)',

    // не более одного пробела подряд
    '(?!.*\\s{2,})',

    // запрет повторения одного символа >6 раз подряд
    '(?!.*(.)\\1{6,})',

    // запрет четырех спецсимволов подряд
    '(?!.*[.\\-—,&\'"«»()]{4,})',

    // минимум одна буква (любая письменность)
    '(?=.*\\p{L})',

    // допустимые символы + длина
    '[\\p{L}\\p{N} .,\\-—&\'"«»()]{2,51}$',
  ].join(''),
  'iu',
);
const nameRegex = /^[А-ЯЁ][а-яё\-\s]+$/;
const emailRegex = /^[A-Za-z0-9](?:[A-Za-z0-9._%+-])*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const passwordRegex = /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/;
const phoneRegex = /^\+7\d{10}$/;

/* Базовый реестр валидаторов для организации */
const organizationBaseValidators: Record<OrganizationBaseFieldKey, AnyYupSchema> = {
    full_title: yup
        .string()
        .typeError('Должно быть строкой')
        .required('Поле обязательно')
        .max(50, 'Поле не должно содержать более 50 символов')
        .matches(
            ORG_NAME_RE,
            'Некорректное название: используйте буквы, цифры и стандартные символы, без повторов и лишних пробелов',
        ),

    short_title: yup
        .string()
        .typeError('Должно быть строкой')
        .required('Данное поле обязательно')
        .max(50, 'Поле не должно содержать более 50 символов')
        .matches(
            ORG_NAME_RE,
            'Некорректное название: используйте буквы, цифры и стандартные символы, без повторов и лишних пробелов',
        ),

    email: yup
        .string()
        .typeError('')
        .max(50, 'Поле не должно содержать более 50 символов')
        .required('Поле обязательно')
        .matches(emailRegex, 'Пожалуйста, введите корректный email'),

    password: yup
        .string()
        .required('Поле обязательно')
        .matches(
            passwordRegex,
            'Пароль должен состоять минимум из 6 латинских символов, хотя бы одной заглавной буквы и одной цифры',
        )
        .max(35, 'Пароль не может быть больше 35 символов'),

    confirm_password: yup
        .string()
        .required('Поле обязательно')
        .max(35, 'Пароль не может быть больше 35 символов')
        .oneOf([yup.ref('password')], 'Пароли не совпадают'),

    phone: yup
        .string()
        .required('Поле обязательно')
        .matches(phoneRegex, 'Введите телефон в формате +7XXXXXXXXXX'),
};

const organizationContactValidators: Record<OrganizationContactFieldKey, AnyYupSchema> = {
    first_name: yup
        .string()
        .required('Поле обязательно')
        .max(50, 'Поле не должно содержать более 50 символов')
        .matches(nameRegex, 'Пожалуйста, введите имя на русском языке. Первая буква заглавная'),

    second_name: yup
        .string()
        .required('Поле обязательно')
        .max(50, 'Поле не должно содержать более 50 символов')
        .matches(nameRegex, 'Пожалуйста, введите фамилию на русском языке. Первая буква заглавная'),

    last_name: yup
        .string()
        .required('Поле обязательно')
        .max(50, 'Поле не должно содержать более 50 символов')
        .matches(nameRegex, 'Пожалуйста, введите отчество на русском языке. Первая буква заглавная'),

    contact_email: yup
        .string()
        .matches(emailRegex, 'Пожалуйста, введите корректный email')
        .max(50, 'Поле не должно содержать более 50 символов')
        .optional(),

    contact_phone: yup
        .string()
        .matches(phoneRegex, 'Введите телефон в формате +7XXXXXXXXXX')
        .optional(),

    contact_person_position: yup
        .string()
        .max(50, 'Поле не должно содержать более 50 символов')
        .optional(),
};

const organizationValidators = {
  ...organizationBaseValidators,
  ...organizationContactValidators,
} as Record<OrganizationFieldKey, AnyYupSchema>;

/**
 * Экспортируем привязанный buildOrganizationSchema, так вы можете вызывать:
 * buildOrganizationSchema(['full_title', ...], { overrideRequired: [...] })
 */
export const buildOrganizationSchema = (fields: OrganizationFieldKey[], opts?: BuildSchemaOptions<OrganizationFieldKey>) =>
    buildSchemaFromBase(organizationValidators, fields, opts);

// Схема для регистрации
export const getOrganizationRegisterSchema = () =>
    buildOrganizationSchema(['full_title', 'short_title', 'email', 'password', 'confirm_password'], {
        // overrideRequired: ['full_title', 'short_title', 'email'],
    });

// Схема для редактирования
export const getOrganizationEditSchema = () =>
    buildOrganizationSchema(
        ['full_title', 'short_title', 'phone'],
        {
            overrideNotRequired: ['phone'],
        },
    );

// Схема для редактирования контакта организации
export const getOrganizationContactEditSchema = () =>
    buildOrganizationSchema(
        [
            'first_name', 'second_name', 'last_name',
            'contact_email', 'contact_phone', 'contact_person_position',
        ],
        {
            overrideNotRequired: ['last_name', 'contact_email'],
        },
    );
